import React, { FC } from 'react';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { StatusBadgeConfig, StatusBadgeProps } from './types';
import Svg from '@styles/icons/svg';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';

const StatusBadge: FC<StatusBadgeProps> = ({
  isClosed,
  isInProgress,
  C,
  separateTextAndIconForMobile,
  config: configFromProps,
  scale = initialScale,
  font = initialFont,
}) => {
  const config: StatusBadgeConfig = {
    running: {
      condition: !isClosed && !!isInProgress,
      state: {
        styles: { backgroundColor: C('ProdJobBatch/Light/Running'), color: C('Label/Light/AlwaysLight Primary') },
        text: 'Running',
        icon: 'runningMan',
        width: 9,
        height: 10.5,
        ...(separateTextAndIconForMobile ? { transform: 'translate(0, 0.5)' } : { transform: 'translate(0, -0.5)' }),
      },
    },
    paused: {
      condition: !isClosed && !isInProgress,
      state: {
        styles: { backgroundColor: C('ProdJobBatch/Light/Paused'), color: C('Label/Light/AlwaysLight Primary') },
        text: 'Paused',
        icon: 'pause',
        width: 6,
        height: 12,
        transform: 'translate(0, -0.3)',
      },
    },
    finished: {
      condition: !!isClosed && !isInProgress,
      state: {
        styles: { backgroundColor: C('ProdJobBatch/Light/Finished'), color: C('Label/Light/AlwaysLight Primary') },
        text: 'Finished',
        icon: 'finishFlag',
        width: 9.5,
        height: 10,
        transform: 'translate(0, -0.5)',
      },
    },
  };

  const currentConfig = Object.values(configFromProps ?? config).find((c) => c.condition)?.state;

  if (!currentConfig) return;

  const IconComponent = (
    <Svg
      icon={currentConfig.icon}
      width={scale(currentConfig.width)}
      height={scale(currentConfig.height)}
      color={currentConfig.styles.color}
      transform={currentConfig?.transform}
    />
  );

  if (separateTextAndIconForMobile) {
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: scale(6),
        }}
      >
        <View style={currentConfig?.styles?.textContainerStyles}>
          <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{i18n.t(currentConfig.text ?? '')}</Text>
        </View>
        <View
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            height: scale(24),
            width: scale(24),
            borderRadius: scale(20),
            backgroundColor: currentConfig.styles.backgroundColor,
            paddingVertical: scale(2.5),
            paddingHorizontal: scale(7),
          }}
        >
          {IconComponent}
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: scale(4),
          height: scale(23),
          borderRadius: scale(20),
          backgroundColor: currentConfig.styles.backgroundColor,
          paddingVertical: scale(2.5),
          paddingHorizontal: scale(7),
        },
        currentConfig.styles.borderWidth
          ? {
              borderWidth: currentConfig.styles.borderWidth,
              borderColor: currentConfig.styles.borderColor,
              borderStyle: currentConfig.styles.borderStyle,
            }
          : {},
        currentConfig.styles.boxStylesOverride,
      ]}
    >
      {IconComponent}
      {currentConfig.text ? (
        <View style={currentConfig?.styles?.textContainerStyles}>
          <Text style={[font.Footnote, { color: currentConfig.styles.color }]}>{i18n.t(currentConfig.text)}</Text>
        </View>
      ) : null}
    </View>
  );
};

export default StatusBadge;
